import { CommonModule } from '@angular/common';
import { Component, HostListener, inject } from '@angular/core';
import { ArchiveSearchStore } from '../../core/archive-search.store';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Timestamp } from '@angular/fire/firestore';
import {
  ARCHIVE_PRESETS,
  DataRangePreset,
} from '../../core/archive-search.types';
import { Booking } from '../../core/models';
import { ArchiveListingComponent } from './archive-listing.component';

@Component({
  selector: 'app-archive',
  imports: [CommonModule, ReactiveFormsModule, ArchiveListingComponent],
  template: `
    <div class="mx-auto max-w-7xl pt-6 sm:px-6 lg:px-8">
      <div class="p-4 bg-white rounded-lg shadow-md">
        <div class="pb-4">
          <h2 class="text-xl font-semibold mb-1">Archive Search</h2>
          <p class="mt-2 text-sm text-gray-700">
            Pick a date range, then search instantly by keywords.
          </p>
        </div>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-5" [formGroup]="form">
          <!-- Preset -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Preset</label
            >
            <select
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-columbus-blue-500"
              [formControl]="form.controls.preset"
              (change)="onPresetChange(form.controls.preset.value)"
            >
              <option *ngFor="let p of presets" [value]="p.value">
                {{ p.label }}
              </option>
            </select>
          </div>

          <!-- Selected Quarter controls -->
          <div
            *ngIf="
              form.controls.preset.value === DataRangePreset.selectedQuarter
            "
          >
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Year</label
            >
            <input
              type="number"
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-columbus-blue-500"
              [formControl]="form.controls.quarterYear"
              (input)="onQuarterChange()"
              min="2000"
              max="2100"
            />
          </div>

          <div
            *ngIf="
              form.controls.preset.value === DataRangePreset.selectedQuarter
            "
          >
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Quarter</label
            >
            <select
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-columbus-blue-500"
              [formControl]="form.controls.quarter"
              (change)="onQuarterChange()"
            >
              <option [value]="1">Q1</option>
              <option [value]="2">Q2</option>
              <option [value]="3">Q3</option>
              <option [value]="4">Q4</option>
            </select>
          </div>

          <!-- Start -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Start</label
            >
            <input
              type="date"
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-columbus-blue-500"
              [formControl]="form.controls.start"
              [readonly]="form.controls.preset.value !== DataRangePreset.custom"
            />
          </div>

          <!-- End + End=Today button -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >End</label
            >
            <div class="flex gap-2">
              <input
                type="date"
                class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-columbus-blue-500"
                [formControl]="form.controls.end"
                [readonly]="
                  form.controls.preset.value !== DataRangePreset.custom
                "
              />
            </div>
          </div>

          <!-- Apply -->
          <div class="flex items-end md:col-span-4">
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-md bg-columbus-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 disabled:opacity-50"
              (click)="onApplyRange()"
              [disabled]="store.isLoading() || form.invalid"
            >
              Apply range
            </button>
          </div>
        </div>

        <div class="mt-4">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Search</label
          >
          <input
            type="text"
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-columbus-blue-500"
            placeholder="Invoice number, tour code, guide name..."
            [formControl]="form.controls.queryText"
            (input)="onQueryChange(form.controls.queryText.value)"
          />
          <p class="mt-2 text-xs text-gray-500">
            Changing keywords does not re-download. It filters cached results.
          </p>
        </div>

        <div
          class="mt-4 flex items-center justify-between text-sm text-gray-700"
        >
          <div
            *ngIf="store.isLoading()"
            class="flex flex-row justify-start text-start"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="200"
              height="200"
              viewBox="0 0 24 24"
              class="fill-columbus-blue-700 size-5 mr-0.5"
            >
              <path
                d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                opacity=".25"
              />
              <path
                d="M10.72,19.9a8,8,0,0,1-6.5-9.79A7.77,7.77,0,0,1,10.4,4.16a8,8,0,0,1,9.49,6.52A1.54,1.54,0,0,0,21.38,12h.13a1.37,1.37,0,0,0,1.38-1.54,11,11,0,1,0-12.7,12.39A1.54,1.54,0,0,0,12,21.34h0A1.47,1.47,0,0,0,10.72,19.9Z"
              >
                <animateTransform
                  attributeName="transform"
                  dur="0.75s"
                  repeatCount="indefinite"
                  type="rotate"
                  values="0 12 12;360 12 12"
                />
              </path>
            </svg>

            Loading bookings…
          </div>
          <div *ngIf="!store.isLoading()">
            Total Bookings in Timerange: {{ store.allInRange().length }}
          </div>
        </div>

        <div
          *ngIf="store.errorMessage()"
          class="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
          {{ store.errorMessage() }}
        </div>

        <!-- Empty State -->

        @if (!store.isLoading() && store.filtered().length === 0) {
          <div
            class="rounded-lg border-2 border-dashed border-gray-200 py-12 text-center text-sm text-gray-400"
          >
            No bookings found for the current filter.
          </div>
        } @else {
          <!-- Replace below with your existing booking list component -->
          <div class="mt-6 space-y-2">
            <div
              *ngFor="let b of store.filtered(); trackBy: trackByBookingId"
              class="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden"
            >
              <app-archive-listing
                [b]="b"
                [store]="this.store"
              ></app-archive-listing>
            </div>
          </div>

          <div class="flex w-full flex-row justify-between pt-2 items-center">
            <p class="text-sm text-gray-600 ">
              Showing {{ store.filtered().length }} of
              {{ store.allInRange().length }} Bookings
            </p>

            <button
              *ngIf="store.filtered().length < store.allInRange().length"
              (click)="store.loadMore()"
              class="inline-flex items-center justify-center rounded-md bg-columbus-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 disabled:opacity-50"
            >
              More...
            </button>

            <button
              *ngIf="showScrollTop"
              (click)="scrollToTop()"
              class="inline-flex items-center justify-center rounded-md bg-columbus-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 disabled:opacity-50"
            >
              ↑
            </button>
          </div>
        }
      </div>
    </div>
  `,
  styles: ``,
  standalone: true,
})
export class ArchiveComponent {
  readonly store = inject(ArchiveSearchStore);
  private readonly nfb = inject(NonNullableFormBuilder);

  readonly DataRangePreset = DataRangePreset;

  readonly presets = ARCHIVE_PRESETS;
  readonly form = this.nfb.group({
    preset: this.nfb.control<DataRangePreset>(DataRangePreset.custom),

    start: this.nfb.control('', { validators: [Validators.required] }),
    end: this.nfb.control('', { validators: [Validators.required] }),

    quarterYear: this.nfb.control<number>(new Date().getFullYear()),
    quarter: this.nfb.control<1 | 2 | 3 | 4>(1),

    queryText: this.nfb.control(''),
  });

  selectedBooking: Booking | null = null;
  selectedBookingModal = false;

  showScrollTop = false;

  @HostListener('window:scroll')
  onScroll(): void {
    // Button erscheint nach z.B. 300px Scroll
    this.showScrollTop = window.scrollY > 300;
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  constructor() {
    this.applyPresetToDates(this.form.controls.preset.value);
  }

  onPresetChange(preset: DataRangePreset): void {
    this.applyPresetToDates(preset);
  }

  onQuarterChange(): void {
    if (this.form.controls.preset.value !== DataRangePreset.selectedQuarter)
      return;
    this.applyPresetToDates(DataRangePreset.selectedQuarter);
  }

  async onApplyRange(): Promise<void> {
    const start = this.parseDateToTimestamp(
      this.form.controls.start.value,
      'start',
    );
    const end = this.parseDateToTimestamp(this.form.controls.end.value, 'end');

    if (!start || !end) return;

    await this.store.setRangeAndFetch({ start, end });
  }

  onQueryChange(value: string): void {
    this.store.queryText.set(value);
  }

  trackByBookingId = (_: number, booking: Booking) => booking.id!;

  //preset Engine

  private applyPresetToDates(preset: DataRangePreset): void {
    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0,
    );
    const todayEnd = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999,
    );

    let start: Date | null = null;
    let end: Date | null = null;

    switch (preset) {
      case DataRangePreset.last7d: {
        end = todayEnd;
        start = new Date(todayStart);
        start.setDate(start.getDate() - 6);
        break;
      }
      case DataRangePreset.last30d: {
        end = todayEnd;
        start = new Date(todayStart);
        start.setDate(start.getDate() - 29);
        break;
      }
      case DataRangePreset.last6M: {
        end = todayEnd;
        start = new Date(todayStart);
        start.setMonth(start.getMonth() - 6);
        break;
      }

      case DataRangePreset.thisYear: {
        end = todayEnd;
        start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        break;
      }
      case DataRangePreset.lastYear: {
        const range = this.getLastYearRange(now);
        start = range.start;
        end = range.end;
        break;
      }

      case DataRangePreset.thisQuarter: {
        const { start: qa, end: qe } = this.getQuarterRange(
          now.getFullYear(),
          this.getQuarter(now),
        );
        start = qa;
        end = qe;
        break;
      }
      case DataRangePreset.lastQuarter: {
        const { year, quarter } = this.getLastQuarter(now);
        const { start: qs, end: qe } = this.getQuarterRange(year, quarter);

        start = qs;
        end = qe;
        break;
      }
      case DataRangePreset.selectedQuarter: {
        const year = this.form.controls.quarterYear.value;
        const q = this.form.controls.quarter.value;
        const { start: qs, end: qe } = this.getQuarterRange(year, q);
        start = qs;
        end = qe;
        break;
      }
      case DataRangePreset.custom:
      default:
        return;
    }

    this.form.controls.start.setValue(this.formatDateInput(start));
    this.form.controls.end.setValue(this.formatDateInput(end));
  }

  private getLastYearRange(date: Date): { start: Date; end: Date } {
    const lastYear = date.getFullYear() - 1;

    const start = new Date(lastYear, 0, 1, 0, 0, 0, 0);
    const end = new Date(lastYear, 11, 31, 23, 59, 59, 999);

    return { start, end };
  }

  private getLastQuarter(date: Date) {
    const currentQuarter = this.getQuarter(date);
    let year = date.getFullYear();
    let quarter: 1 | 2 | 3 | 4;

    if (currentQuarter === 1) {
      // Last quarter is Q4 of previous year
      year = year - 1;
      quarter = 4;
    } else {
      quarter = (currentQuarter - 1) as 1 | 2 | 3 | 4;
    }

    return { year, quarter };
  }

  private getQuarter(date: Date): 1 | 2 | 3 | 4 {
    const m = date.getMonth();
    if (m <= 2) return 1;
    if (m <= 5) return 2;
    if (m <= 8) return 3;
    return 4;
  }

  private getQuarterRange(
    year: number,
    quarter: 1 | 2 | 3 | 4,
  ): { start: Date; end: Date } {
    const startMonth = (quarter - 1) * 3; //0,3,6,9
    const start = new Date(year, startMonth, 1, 0, 0, 0, 0);
    const end = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);
    return { start, end };
  }

  private formatDateInput(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseDateToTimestamp(
    value: string,
    boundry: 'start' | 'end',
  ): Timestamp | null {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;

    const date =
      boundry === 'start'
        ? new Date(y, m - 1, d, 0, 0, 0, 0)
        : new Date(y, m - 1, d, 23, 59, 59, 999);

    return Timestamp.fromDate(date);
  }
}
